import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  useGetContactQuery, 
  useGetMessagesByContactQuery,
  useGetWhatsappPhonesQuery,
  useSendMessageMutation,
  useSendMediaMessageMutation,
  useDeleteMessageMutation,
} from '../store/services/whatsappApi';
import { useGetTemplatesQuery, Template as TemplateType } from '../store/services/whatsappApi';
import { Message as MessageType } from '../store/services/whatsappApi';
import MessageBubble from '../components/messages/MessageBubble';
import DeleteConfirmDialog from '../components/messages/DeleteConfirmDialog';
import { websocketService, WebSocketMessage } from '../services/websocketService';
import { useDispatch } from 'react-redux';
// Removed unused imports
// import { useSelector } from 'react-redux';
// import { RootState } from '../store/store';
import { markContactRead } from '../store/slices/messagesSlice';

interface MessagesProps {
  contactId?: number;
}

const MessagesWithDelete: React.FC<MessagesProps> = ({ contactId: propContactId }) => {
  // Use prop contactId if provided, otherwise use URL param
  const { contactId: urlContactId } = useParams<{ contactId: string }>();
  const contactIdNum = propContactId || (urlContactId ? parseInt(urlContactId) : 0);
  
  // State for message input
  const [newMessage, setNewMessage] = useState('');
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'text' | 'image' | 'video' | 'document' | 'audio'>('text');
  
  // State for message actions
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Fetch contact details, WhatsApp phones, and messages
  const { data: contact, isLoading: isContactLoading } = useGetContactQuery(contactIdNum);
  const { data: messages, isLoading: isMessagesLoading, refetch: refetchMessages } = useGetMessagesByContactQuery(contactIdNum);
  const { data: whatsappPhones } = useGetWhatsappPhonesQuery();
  const { data: templates } = useGetTemplatesQuery({});
  
  // Access Redux state and dispatch
  const dispatch = useDispatch();
  // Commented out unused variable
  // const unreadCounts = useSelector((state: RootState) => state.messages.unreadCounts);
  
  // Real mutations for sending messages
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [sendMediaMessage, { isLoading: isSendingMedia }] = useSendMediaMessageMutation();
  const [deleteMessage, { isLoading: isDeleting }] = useDeleteMessageMutation();

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Initial scroll to bottom when messages load
  useEffect(() => {
    if (!isMessagesLoading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView();
    }
  }, [isMessagesLoading]);

  // Set default WhatsApp phone ID if available
  useEffect(() => {
    if (whatsappPhones && whatsappPhones.length > 0 && !selectedPhoneId) {
      setSelectedPhoneId(whatsappPhones[0].id);
    }
  }, [whatsappPhones, selectedPhoneId]);
  
  // Set up WebSocket message handler
  useEffect(() => {
    // Add WebSocket message handler
    const removeHandler = websocketService.addMessageHandler((data: WebSocketMessage) => {
      // If this is a new message for the current contact, refetch messages
      if (data.type === 'new_message' && data.contact_id === contactIdNum) {
        console.log('Received real-time message for current contact, refetching...');
        refetchMessages();
        
        // Mark messages as read when viewing this contact
        if (contactIdNum) {
          dispatch(markContactRead(contactIdNum));
        }
      }
    });
    
    // Make sure WebSocket is connected
    if (!websocketService.isConnected()) {
      console.log('WebSocket not connected, connection should be handled by auth component');
    }
    
    // Cleanup handler on unmount
    return () => {
      removeHandler();
    };
  }, [contactIdNum, refetchMessages, dispatch]);

  // Helper: replace {{variable}} with simple values when known, otherwise placeholder
  const replaceTemplateVars = (text: string, vars: Record<string, string> = {}) => {
    if (!text) return '';
    return text.replace(/{{([^}]+)}}/g, (_match, name) => {
      const key = String(name).trim();
      return (vars[key] && vars[key].length > 0) ? vars[key] : `[${key}]`;
    });
  };

  // Render a template message bubble content using template definition
  const renderTemplateMessageContent = (msg: MessageType) => {
    if (!templates || templates.length === 0) {
      return msg.content ? <p>{msg.content}</p> : null;
    }
    // Attempt to locate template by name (msg.content) or id string
    const t = templates.find((tpl: TemplateType) => {
      return tpl.name === msg.content || String(tpl.id) === String(msg.content);
    }) as any;
    if (!t || !t.components) {
      return msg.content ? <p>{msg.content}</p> : null;
    }
    const header = t.components.find((c: any) => c.type === 'HEADER');
    const body = t.components.find((c: any) => c.type === 'BODY');
    const footer = t.components.find((c: any) => c.type === 'FOOTER');
    const buttonsComp = t.components.find((c: any) => c.type === 'BUTTONS');
    const buttons = (buttonsComp && buttonsComp.buttons) ? buttonsComp.buttons : [];
    const selectedPhone = (whatsappPhones || []).find((p: any) => p.id === selectedPhoneId);
    const phoneNumberId = selectedPhone?.phone_number_id;
    const mediaHandle = (t as any).media_handle;
    const proxyUrl = mediaHandle
      ? `/api/v1/media-proxy/${mediaHandle}${phoneNumberId ? `?phone_number_id=${phoneNumberId}` : ''}`
      : null;

    return (
      <div className="template-message-render">
        {/* Header (TEXT or media) */}
        {header && header.format === 'TEXT' && header.text && (
          <div className="font-medium mb-1">{replaceTemplateVars(header.text)}</div>
        )}
        {header && header.format === 'IMAGE' && proxyUrl && (
          <div className="mb-2 rounded-lg overflow-hidden">
            <img src={proxyUrl} alt="Template media" className="max-w-full h-auto rounded" />
          </div>
        )}
        {header && header.format === 'VIDEO' && proxyUrl && (
          <div className="mb-2 rounded-lg overflow-hidden">
            <video src={proxyUrl} controls className="max-w-full h-auto rounded" />
          </div>
        )}
        {header && header.format === 'DOCUMENT' && proxyUrl && (
          <div className="flex items-center bg-gray-50 rounded-lg p-2 mb-2">
            <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <a href={proxyUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
              View document
            </a>
          </div>
        )}
        {/* Body */}
        {body && body.text && (
          <div className="text-gray-800 whitespace-pre-wrap mb-1">{replaceTemplateVars(body.text)}</div>
        )}
        {/* Footer */}
        {footer && footer.text && (
          <div className="text-gray-500 text-xs">{replaceTemplateVars(footer.text)}</div>
        )}
        {/* Buttons (labels only) */}
        {buttons.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
            {buttons.map((b: any, i: number) => (
              <div key={i} className="text-center">
                <span className="text-whatsapp-teal text-sm font-medium">{b.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // WhatsApp supported media types
  const WHATSAPP_SUPPORTED_TYPES = {
    image: ['image/jpeg', 'image/png'],
    video: ['video/mp4', 'video/3gpp'],
    audio: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'],
    document: ['application/pdf', 'application/vnd.ms-powerpoint', 'application/msword', 
               'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
               'application/vnd.openxmlformats-officedocument.presentationml.presentation',
               'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
  };

  const MAX_FILE_SIZES = {
    image: 5 * 1024 * 1024,      // 5MB
    video: 16 * 1024 * 1024,     // 16MB
    audio: 16 * 1024 * 1024,     // 16MB
    document: 100 * 1024 * 1024  // 100MB
  };

  // Handle file selection with WhatsApp validation
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    let mediaType: 'image' | 'video' | 'audio' | 'document' | null = null;
    let isSupported = false;

    // Check which category the file belongs to
    for (const [type, mimeTypes] of Object.entries(WHATSAPP_SUPPORTED_TYPES)) {
      if (mimeTypes.includes(file.type)) {
        mediaType = type as 'image' | 'video' | 'audio' | 'document';
        isSupported = true;
        break;
      }
    }

    if (!isSupported || !mediaType) {
      alert(`Unsupported file type: ${file.type}\n\nWhatsApp only supports:\n` +
            `Images: JPEG, PNG\n` +
            `Videos: MP4, 3GPP\n` +
            `Audio: AAC, MP4, MPEG, AMR, OGG\n` +
            `Documents: PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX`);
      e.target.value = ''; // Clear the input
      return;
    }

    // Validate file size
    const maxSize = MAX_FILE_SIZES[mediaType];
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      alert(`File too large! Maximum size for ${mediaType} is ${sizeMB}MB.\nYour file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
      e.target.value = ''; // Clear the input
      return;
    }

    setSelectedFile(file);
    setMessageType(mediaType);
    
    // Create preview for images and videos
    if (mediaType === 'image' || mediaType === 'video') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setMediaPreview(null);
    }
  };

  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null);
    setMediaPreview(null);
    setMessageType('text');
  };

  // Send text or media message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPhoneId || !contactIdNum) return;
    
    // Check if we have either text or file
    if (!newMessage.trim() && !selectedFile) return;
    
    try {
      if (selectedFile) {
        // Send media message using RTK Query mutation
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('contact_id', contactIdNum.toString());
        formData.append('whatsapp_phone_id', selectedPhoneId.toString());
        formData.append('recipient_phone', contact?.phone_number || '');
        formData.append('message_type', messageType);
        if (newMessage.trim()) {
          formData.append('content', newMessage);
        }

        await sendMediaMessage(formData).unwrap();
        
        clearSelectedFile();
        setNewMessage('');
      } else {
        // Send text message
        await sendMessage({
          whatsapp_phone_id: selectedPhoneId,
          contact_id: contactIdNum,
          content: newMessage,
          message_type: 'text',
          recipient_phone: contact?.phone_number || ''
        }).unwrap();
        setNewMessage('');
      }
      
      // Refresh conversation
      refetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  if (isContactLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">Contact not found</h2>
          <p className="text-gray-600 mt-2">The contact you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="flex flex-col h-full bg-gray-100 overflow-hidden flex-1">
      {/* Header */}
      <div className="bg-whatsapp-green text-white p-4 shadow-md flex items-center">
        <div className="w-12 h-12 rounded-full bg-white text-whatsapp-green flex items-center justify-center font-bold text-xl mr-3">
          {contact.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-xl font-bold">{contact.name}</h1>
          <p className="text-sm">{contact.phone_number}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-100 flex flex-col max-h-[calc(100vh-180px)]">
        {isMessagesLoading ? (
          <div className="flex justify-center p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : (
          <div className="space-y-4 mt-auto">
            {messages && messages.length > 0 ? (
              [...messages].reverse().map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  onDelete={(messageId) => {
                    setMessageToDelete(messageId);
                    setShowDeleteConfirm(true);
                  }}
                  renderTemplateContent={renderTemplateMessageContent}
                />
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                No messages yet. Start a conversation!
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="bg-white p-4 border-t sticky bottom-0">
        {/* Media Preview */}
        {selectedFile && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {mediaPreview && messageType === 'image' && (
                  <img src={mediaPreview} alt="Preview" className="max-h-32 rounded" />
                )}
                {mediaPreview && messageType === 'video' && (
                  <video src={mediaPreview} className="max-h-32 rounded" controls />
                )}
                {messageType === 'document' && (
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-700">{selectedFile.name}</span>
                  </div>
                )}
                {messageType === 'audio' && (
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                    <span className="text-sm text-gray-700">{selectedFile.name}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={clearSelectedFile}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          {whatsappPhones && whatsappPhones.length > 0 && (
            <select
              className="border rounded-md px-3 bg-gray-50"
              value={selectedPhoneId || ''}
              onChange={(e) => setSelectedPhoneId(parseInt(e.target.value))}
              disabled={isSending || isSendingMedia}
            >
              {whatsappPhones.map((phone: any) => (
                <option key={phone.id} value={phone.id}>
                  {phone.display_name || phone.phone_number}
                </option>
              ))}
            </select>
          )}
          
          {/* File Upload Button */}
          <label className="cursor-pointer text-gray-500 hover:text-whatsapp-green p-2 rounded-full hover:bg-gray-100">
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,video/mp4,video/3gpp,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
              onChange={handleFileSelect}
              disabled={isSending || isSendingMedia}
            />
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </label>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={selectedFile ? "Add a caption (optional)" : "Type a message"}
            className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
            disabled={isSending || isSendingMedia}
          />
          
          <button
            type="submit"
            className="bg-whatsapp-green text-white p-2 rounded-full hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-whatsapp-dark-green"
            disabled={isSending || isSendingMedia || (!newMessage.trim() && !selectedFile)}
          >
            {(isSending || isSendingMedia) ? (
              <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>

    {/* Delete Message Confirmation Dialog */}
    <DeleteConfirmDialog 
      isOpen={showDeleteConfirm}
      isDeleting={isDeleting}
      onCancel={() => {
        setShowDeleteConfirm(false);
        setMessageToDelete(null);
      }}
      onConfirm={async () => {
        if (messageToDelete !== null) {
          try {
            await deleteMessage({ messageId: messageToDelete });
            refetchMessages(); // Refresh messages after deletion
          } catch (error) {
            console.error('Failed to delete message:', error);
          } finally {
            setShowDeleteConfirm(false);
            setMessageToDelete(null);
          }
        }
      }}
    />
    </>
  );
};

export default MessagesWithDelete;
